import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * DOM attribute StickyBar looks up; its value is the scroll point name. Must
 * match hen-ui's `SCROLL_POINT_ATTRIBUTE`. It is restated rather than imported
 * so this package keeps working with hen-ui versions that predate StickyBar.
 */
export const SCROLL_POINT_ATTRIBUTE = 'data-hen-scroll-point';

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function readScrollPointName(
  meta: Readonly<Record<string, unknown>> | undefined,
): string {
  const raw = meta?.scrollPoint;
  if (typeof raw !== 'string') return '';
  return raw.trim();
}

/**
 * True when an element under `scope` carries this scroll point name. Values
 * are compared rather than interpolated into a selector, so any author-typed
 * name is safe.
 */
export function hasScrollPoint(scope: ParentNode, name: string): boolean {
  const blocks = scope.querySelectorAll(`[${SCROLL_POINT_ATTRIBUTE}]`);
  return Array.from(blocks).some(
    (block) => block.getAttribute(SCROLL_POINT_ATTRIBUTE) === name,
  );
}

function elementsBetween(start: Element, end: Element): Element[] {
  const elements: Element[] = [];
  let current = start.nextElementSibling;
  while (current && current !== end) {
    elements.push(current);
    current = current.nextElementSibling;
  }
  return elements;
}

function carriesScrollPoint(block: Element, name: string): boolean {
  return (
    block.getAttribute(SCROLL_POINT_ATTRIBUTE) === name ||
    hasScrollPoint(block, name)
  );
}

type ScrollPointResult =
  | { name: string; status: 'checking' }
  | { name: string; status: 'forwarded' }
  | { name: string; status: 'stamped'; host: Element | null };

/**
 * The name is passed to the block as a DOM attribute. When the block does
 * not forward it, the attribute is set on the block's own root element
 * instead, so no element is ever inserted between the block and its parent.
 * Layouts such as Split size and pin their direct children, so a wrapper,
 * even a `display: contents` one, would break them.
 *
 * Two inert `<template>` probes bracket the block only while it is checked,
 * which tells the block's root apart from its siblings. The check runs
 * before paint and again whenever the block replaces its root element.
 */
export function ScrollPointFallback({
  name,
  children,
}: {
  readonly name: string;
  readonly children: ReactNode;
}) {
  const startRef = useRef<HTMLTemplateElement>(null);
  const endRef = useRef<HTMLTemplateElement>(null);
  const [result, setResult] = useState<ScrollPointResult>({
    name,
    status: 'checking',
  });
  const current: ScrollPointResult =
    result.name === name ? result : { name, status: 'checking' };
  const host = current.status === 'stamped' ? current.host : null;

  useIsomorphicLayoutEffect(() => {
    if (current.status !== 'checking') return;
    const start = startRef.current;
    const end = endRef.current;
    if (!start || !end) return;
    const blocks = elementsBetween(start, end);
    if (blocks.some((block) => carriesScrollPoint(block, name))) {
      setResult({ name, status: 'forwarded' });
      return;
    }
    const root = blocks[0] ?? null;
    root?.setAttribute(SCROLL_POINT_ATTRIBUTE, name);
    setResult({ name, status: 'stamped', host: root });
  }, [name, current.status]);

  useEffect(() => {
    const parent = host?.parentNode;
    if (!host || !parent) return;
    const view = host.ownerDocument.defaultView;
    const Observer =
      (view as (Window & typeof globalThis) | null)?.MutationObserver ??
      MutationObserver;
    const recheck = () => {
      if (!host.isConnected || host.parentNode !== parent) {
        setResult({ name, status: 'checking' });
      }
    };
    const replaced = new Observer(recheck);
    replaced.observe(parent, { childList: true });
    return () => {
      replaced.disconnect();
      if (host.getAttribute(SCROLL_POINT_ATTRIBUTE) === name) {
        host.removeAttribute(SCROLL_POINT_ATTRIBUTE);
      }
    };
  }, [host, name]);

  // The probe slots stay in place as `null` once checked, so the block keeps
  // its position and React does not remount it when the probes go away.
  const checking = current.status === 'checking';
  return (
    <>
      {checking ? <template ref={startRef} /> : null}
      {children}
      {checking ? <template ref={endRef} /> : null}
    </>
  );
}
